import {
  BaseProductsClient,
  Parhato2ProductSubscription,
  ProductResults,
  ProductReviewPayload,
  ProductsQueryParams,
  Product,
  SearchProductsQueryParams,
  ProductReview,
  SaleTypeEnum,
} from "@core/types";
import { IrskomMainPageProducts, IrskomProductDetails } from "./types";
import { converterIrskomProductList, converterIrskomProductDetails } from "./converters";
class ProductsClient extends BaseProductsClient {
  async getProducts(params: ProductsQueryParams): Promise<ProductResults> {
    const { categoryId, pageSize, pageNumber, minPrice, maxPrice } = params;
    const saleType = (params.featured && SaleTypeEnum.featured) || (params.on_sale && SaleTypeEnum.on_sale);
    const { data } = await this.instance.get<IrskomMainPageProducts>("commodity/list/filtered", {
      params: {
        PageSize: pageSize,
        PageNumber: pageNumber,
        CategoryId: categoryId,
        SaleType: saleType,
        MinPrice: minPrice,
        MaxPrice: maxPrice,
        // sortOrder: sortOrder,
      },
    });

    return {
      products: data?.items.map(converterIrskomProductList),
      additionalFeatures: [],
    };
  }

  async getProductById(id: string): Promise<Product> {
    const { data } = await this.instance.get<IrskomProductDetails>(`commodity/${id}`);

    return converterIrskomProductDetails(data);
  }

  async searchProducts(params: SearchProductsQueryParams): Promise<Product[]> {
    const { data } = await this.instance.get<IrskomMainPageProducts>(`search/result`, {
      params: {
        searchText: params.keyword,
        pageNumber: params.pageNumber,
        additionalFeature: params.additionalFeature,
      },
    });

    return data?.items.map(converterIrskomProductList);
  }

  async getNewestProducts({
    categoryId,
    pageSize,
    pageNumber,
  }: Partial<ProductsQueryParams> = {}): Promise<ProductResults> {
    const { data } = await this.instance.get<IrskomMainPageProducts>("commodity/list/filtered", {
      params: {
        PageSize: pageSize,
        PageNumber: pageNumber,
        SaleType: "3",
        CategoryId: categoryId,
      },
    });

    return {
      products: data?.items.map(converterIrskomProductList),
      additionalFeatures: [],
    };
  }

  async getOnSaleProducts({ categoryId, pageSize, pageNumber }: Partial<ProductsQueryParams> = {}) {
    const { data } = await this.instance.get<IrskomMainPageProducts>("commodity/list/filtered", {
      params: {
        PageSize: pageSize,
        PageNumber: pageNumber,
        SaleType: "2",
        CategoryId: categoryId,
      },
    });

    return {
      products: data?.items.map(converterIrskomProductList),
      additionalFeatures: [],
    };
  }

  async getFeaturedProducts({
    categoryId,
    pageSize,
    pageNumber,
  }: Partial<ProductsQueryParams> = {}): Promise<ProductResults> {
    const { data } = await this.instance.get<IrskomMainPageProducts>("commodity/list/filtered", {
      params: {
        PageSize: pageSize,
        PageNumber: pageNumber,
        SaleType: "1",
        CategoryId: categoryId,
      },
    });

    return {
      products: data?.items.map(converterIrskomProductList),
      additionalFeatures: [],
    };
  }

  async getIncludeProducts(include: string[], params?: { per_page?: string }) {
    const requests: Promise<any>[] = [];
    include.forEach((id) => {
      requests.push(this.getProductById(id));
    });

    return await Promise.all(requests);
  }

  async getCustomersChoiceProducts({
    categoryId,
    pageSize,
    pageNumber,
  }: Partial<ProductsQueryParams> = {}): Promise<ProductResults> {
    const { data } = await this.instance.get<IrskomMainPageProducts>("commodity/list/filtered", {
      params: {
        PageSize: pageSize,
        PageNumber: pageNumber,
        SaleType: "1",
        CategoryId: categoryId,
      },
    });

    return {
      products: data?.items.map(converterIrskomProductList),
      additionalFeatures: [],
    };
  }

  async getProductReviews(id: string): Promise<ProductReview[]> {
    throw new Error("getProductReviews is not implemented");
  }

  async addProductReview(productReviewPayload: ProductReviewPayload): Promise<any> {
    throw new Error("addProductReview is not implemented");
  }

  async addProductSubscription(productId: string): Promise<any> {
    throw new Error("addProductSubscription is not implemented");
  }

  async getAllProductsSubscriptions(): Promise<Parhato2ProductSubscription[]> {
    throw new Error("getAllProductsSubscriptions is not implemented");
  }

  async getProductsFromParentAndChildCategories(params: ProductsQueryParams): Promise<Product[]> {
    throw new Error("getProductsFromParentAndChildCategories not implemented");
  }

  async getAdditionalProductFilters(): Promise<any[]> {
    throw new Error("getAdditionalProductFilters not implemented");
  }
}

export default ProductsClient;
